import { SearchInput } from '@components/search/SearchInput'
import { FilterField, TableFilterPanel } from '@components/table/filters'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { Combobox } from '@/components/ui/combobox'
import { DatePicker } from '@/components/ui/date-picker'

const FILTER_KEYS = [
  'roles',
  'date_joined_from',
  'date_joined_to',
  'is_partner',
  'was_contacted'
] as const

interface Filters {
  roles?: string
  date_joined_from?: string
  date_joined_to?: string
  is_partner?: string
  was_contacted?: string
}

function parseFiltersFromParams(searchParams: URLSearchParams): Filters {
  return {
    roles: searchParams.get('roles') || undefined,
    date_joined_from: searchParams.get('date_joined_from') || undefined,
    date_joined_to: searchParams.get('date_joined_to') || undefined,
    is_partner: searchParams.get('is_partner') || undefined,
    was_contacted: searchParams.get('was_contacted') || undefined
  }
}

export function UsersTableFilters() {
  const { t } = useTranslation('users')
  const [searchParams, setSearchParams] = useSearchParams()

  const [filtersUI, setFiltersUI] = useState<Filters>(() =>
    parseFiltersFromParams(searchParams)
  )
  const [filters, setFilters] = useState<Filters>(() =>
    parseFiltersFromParams(searchParams)
  )

  const areEqual = (a: Filters, b: Filters) =>
    JSON.stringify(a) === JSON.stringify(b)

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (areEqual(filtersUI, filters)) return

      setFilters(filtersUI)

      const params = new URLSearchParams(searchParams)

      FILTER_KEYS.forEach((key) => params.delete(key))
      params.delete('offset')

      if (filtersUI.roles) params.set('roles', filtersUI.roles)
      if (filtersUI.date_joined_from)
        params.set('date_joined_from', filtersUI.date_joined_from)
      if (filtersUI.date_joined_to)
        params.set('date_joined_to', filtersUI.date_joined_to)
      if (filtersUI.is_partner) params.set('is_partner', filtersUI.is_partner)
      if (filtersUI.was_contacted)
        params.set('was_contacted', filtersUI.was_contacted)

      setSearchParams(params)
    }, 400)

    return () => clearTimeout(timeout)
  }, [filtersUI])

  const hasActiveFilters = useMemo(
    () =>
      filtersUI.roles !== undefined ||
      filtersUI.date_joined_from !== undefined ||
      filtersUI.date_joined_to !== undefined ||
      filtersUI.is_partner !== undefined ||
      filtersUI.was_contacted !== undefined,
    [filtersUI]
  )

  const handleClear = useCallback(() => {
    setFiltersUI({})
  }, [])

  const roleOptions = useMemo(
    () => [
      { label: t('filters.roles.admin'), value: 'ADMIN' },
      { label: t('filters.roles.user'), value: 'USER' },
      { label: t('filters.roles.roommate'), value: 'ROOMMATE' },
      { label: t('filters.roles.landlord'), value: 'LANDLORD' }
    ],
    [t]
  )

  const partnerOptions = useMemo(
    () => [
      { label: t('filters.partner.yes'), value: 'true' },
      { label: t('filters.partner.no'), value: 'false' }
    ],
    [t]
  )

  const contactedOptions = useMemo(
    () => [
      { label: t('filters.contacted.yes'), value: 'true' },
      { label: t('filters.contacted.no'), value: 'false' }
    ],
    [t]
  )

  return (
    <div className="space-y-4">
      <SearchInput />

      <TableFilterPanel
        title={t('filters.title')}
        clearLabel={t('filters.clear')}
        hasActiveFilters={hasActiveFilters}
        onClear={handleClear}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <FilterField label={t('filters.role')}>
            <Combobox
              clearable
              searchable={false}
              placeholder={t('filters.role')}
              value={filtersUI.roles}
              onChange={(v) => setFiltersUI({ ...filtersUI, roles: v })}
              options={roleOptions}
            />
          </FilterField>

          <div className="sm:col-span-2">
            <FilterField label={t('filters.dateJoinedRange')}>
              <div className="grid grid-cols-2 gap-2">
                <DatePicker
                  clearable
                  placeholder={t('filters.from')}
                  max={filtersUI.date_joined_to}
                  value={filtersUI.date_joined_from}
                  onChange={(v) =>
                    setFiltersUI({ ...filtersUI, date_joined_from: v })
                  }
                />
                <DatePicker
                  clearable
                  placeholder={t('filters.to')}
                  min={filtersUI.date_joined_from}
                  value={filtersUI.date_joined_to}
                  onChange={(v) =>
                    setFiltersUI({ ...filtersUI, date_joined_to: v })
                  }
                />
              </div>
            </FilterField>
          </div>

          <FilterField label={t('filters.partner.label')}>
            <Combobox
              clearable
              searchable={false}
              placeholder={t('filters.partner.label')}
              value={filtersUI.is_partner}
              onChange={(v) => setFiltersUI({ ...filtersUI, is_partner: v })}
              options={partnerOptions}
            />
          </FilterField>

          <FilterField label={t('filters.contacted.label')}>
            <Combobox
              clearable
              searchable={false}
              placeholder={t('filters.contacted.label')}
              value={filtersUI.was_contacted}
              onChange={(v) => setFiltersUI({ ...filtersUI, was_contacted: v })}
              options={contactedOptions}
            />
          </FilterField>
        </div>
      </TableFilterPanel>
    </div>
  )
}
